import { Helmet } from 'react-helmet-async';

const BUSINESS_ID = 'https://2aelectronics.com/#business';
export const SITE_URL = 'https://2aelectronics.com';

// Helper: build a localized LocalBusiness schema linked to the global #business node.
// Use this on city / state / area pages so Google knows the business serves that area.
export const buildLocalBusinessSchema = ({ city, state, stateAbbr, lat, lng, ratingValue = '5.0', reviewCount = '389' }) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#business-${(city || 'us').toLowerCase().replace(/\s+/g, '-')}`,
  name: '2A Electronics Service',
  image: `${SITE_URL}/logo.png`,
  url: SITE_URL,
  telephone: '+17737665814',
  priceRange: '$$',
  parentOrganization: { '@id': BUSINESS_ID },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '4859 W Irving Park Rd',
    addressLocality: 'Chicago',
    addressRegion: 'IL',
    postalCode: '60641',
    addressCountry: 'US'
  },
  geo: { '@type': 'GeoCoordinates', latitude: 41.953319, longitude: -87.748791 },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '10:00',
      closes: '18:00'
    },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '10:00', closes: '16:00' }
  ],
  areaServed: city
    ? [
        { '@type': 'City', name: city, ...(state ? { containedInPlace: { '@type': 'State', name: state } } : {}) },
        ...(state ? [{ '@type': 'State', name: state }] : []),
        { '@type': 'Country', name: 'United States' }
      ]
    : [
        { '@type': 'City', name: 'Chicago' },
        { '@type': 'AdministrativeArea', name: 'Irving Park, Chicago' },
        { '@type': 'AdministrativeArea', name: 'Logan Square, Chicago' },
        { '@type': 'AdministrativeArea', name: 'Avondale, Chicago' },
        { '@type': 'AdministrativeArea', name: 'Portage Park, Chicago' },
        { '@type': 'Country', name: 'United States' }
      ],
  ...(lat && lng ? { hasMap: `https://www.google.com/maps?q=${lat},${lng}` } : {}),
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue,
    reviewCount,
    bestRating: '5',
    worstRating: '1'
  },
  sameAs: [
    'https://www.instagram.com/2aelectronicsservice/',
    'https://www.facebook.com/2AElectronicsService/',
    'https://www.yelp.com/biz/2a-electronics-service-chicago',
    'https://www.google.com/maps/place/2A+Electronics+Service/'
  ]
});

// Helper: build FAQPage schema from {q, a} array, surfaces as expandable answers in Google.
export const buildFAQSchema = (faqs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs
    .filter(f => f && (f.q || f.question) && (f.a || f.answer))
    .map(f => ({
      '@type': 'Question',
      name: f.q || f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a || f.answer
      }
    }))
});

// Helper: build a Service schema linked to the business node.
export const buildServiceSchema = ({ name, description, areaCity, areaState, serviceType = 'Electronics Repair', url }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  serviceType,
  provider: { '@id': BUSINESS_ID },
  ...(url ? { url: url.startsWith('http') ? url : `${SITE_URL}${url}` } : {}),
  areaServed: areaCity
    ? { '@type': 'City', name: areaCity, ...(areaState ? { containedInPlace: { '@type': 'State', name: areaState } } : {}) }
    : { '@type': 'Country', name: 'United States' }
});

// Helper: build Review schema (multiple reviews → ItemList of Review).
export const buildReviewListSchema = (reviews = []) => {
  if (!reviews.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: reviews.slice(0, 30).map((r, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Review',
        author: { '@type': 'Person', name: r.name || r.author || 'Verified Customer' },
        reviewRating: { '@type': 'Rating', ratingValue: String(r.rating || 5), bestRating: '5' },
        reviewBody: r.text || r.review_text || r.body || '',
        ...(r.date ? { datePublished: r.date } : {}),
        itemReviewed: { '@id': BUSINESS_ID }
      }
    }))
  };
};

// Helper: Speakable schema marks specific selectors as voice-friendly.
// Eligible for Google Assistant / voice search results.
export const buildSpeakableSchema = (cssSelectors = ['h1', 'h2', '[data-speakable]']) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: cssSelectors
  }
});

// Helper: HowTo schema for repair guide pages → eligible for "Step 1, Step 2..." rich result.
export const buildHowToSchema = ({ name, description, totalTime, steps = [], image }) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name,
  description,
  ...(totalTime ? { totalTime } : {}),
  ...(image ? { image } : {}),
  step: steps.map((s, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: s.name || s.title || `Step ${i + 1}`,
    text: s.text || s.description || ''
  }))
});

// Approximate geo coordinates for top US cities served via mail-in.
// Used to anchor each /repair-city/{slug} page to its real geographic location.
export const CITY_GEO = {
  'chicago': { lat: 41.8781, lng: -87.6298 },
  'new-york': { lat: 40.7128, lng: -74.0060 },
  'los-angeles': { lat: 34.0522, lng: -118.2437 },
  'houston': { lat: 29.7604, lng: -95.3698 },
  'phoenix': { lat: 33.4484, lng: -112.0740 },
  'philadelphia': { lat: 39.9526, lng: -75.1652 },
  'san-antonio': { lat: 29.4241, lng: -98.4936 },
  'san-diego': { lat: 32.7157, lng: -117.1611 },
  'dallas': { lat: 32.7767, lng: -96.7970 },
  'austin': { lat: 30.2672, lng: -97.7431 },
  'jacksonville': { lat: 30.3322, lng: -81.6557 },
  'san-jose': { lat: 37.3382, lng: -121.8863 },
  'fort-worth': { lat: 32.7555, lng: -97.3308 },
  'columbus': { lat: 39.9612, lng: -82.9988 },
  'charlotte': { lat: 35.2271, lng: -80.8431 },
  'indianapolis': { lat: 39.7684, lng: -86.1581 },
  'san-francisco': { lat: 37.7749, lng: -122.4194 },
  'seattle': { lat: 47.6062, lng: -122.3321 },
  'denver': { lat: 39.7392, lng: -104.9903 },
  'washington-dc': { lat: 38.9072, lng: -77.0369 },
  'nashville': { lat: 36.1627, lng: -86.7816 },
  'atlanta': { lat: 33.7490, lng: -84.3880 },
  'miami': { lat: 25.7617, lng: -80.1918 },
  'boston': { lat: 42.3601, lng: -71.0589 },
  'las-vegas': { lat: 36.1699, lng: -115.1398 },
  'portland': { lat: 45.5152, lng: -122.6784 },
  'detroit': { lat: 42.3314, lng: -83.0458 },
  'minneapolis': { lat: 44.9778, lng: -93.2650 }
};

const SEO = ({
  title = '2A Electronics Service | Phone Repair Chicago',
  description = "Chicago's #1 electronics repair shop. Expert iPhone, Samsung, MacBook, PlayStation repair. Same-day service, 1-month warranty.",
  keywords = 'phone repair Chicago, iPhone repair, Samsung repair, laptop repair, sell phone',
  canonical,
  image = 'https://2aelectronics.com/og-image.jpg',
  type = 'website',
  noindex = false,
  breadcrumbs = null,
  schema = null,
  schemas = null,        // array of additional JSON-LD blocks
  hreflangs = null,      // [{lang:'es', path:'/es/...'}, {lang:'pl', path:'/pl/...'}]
  geo = null,            // {lat, lng} - emits og:place + geo meta tags
  speakable = false      // when true, auto-emits a Speakable schema for h1/h2
}) => {
  const fullTitle = title.includes('2A Electronics') ? title : `${title} | 2A Electronics Service`;

  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((b, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: b.name,
      item: b.url ? `${SITE_URL}${b.url}` : undefined
    }))
  } : null;

  const extraSchemas = (schemas || []).filter(Boolean);
  if (speakable) {
    extraSchemas.push(buildSpeakableSchema());
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {canonical && <link rel="canonical" href={`${SITE_URL}${canonical}`} />}

      {/* HrefLang for multilingual pages, tells Google which language version to serve in which region */}
      {hreflangs && hreflangs.map(h => (
        <link key={`hreflang-${h.lang}`} rel="alternate" hrefLang={h.lang} href={`${SITE_URL}${h.path}`} />
      ))}
      {hreflangs && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${(hreflangs[0] && hreflangs[0].path) || canonical || '/'}`} />}

      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical ? `${SITE_URL}${canonical}` : SITE_URL} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="2A Electronics Service" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical ? `${SITE_URL}${canonical}` : SITE_URL} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Geo meta tags - anchor page to a specific physical location */}
      {geo && geo.lat && geo.lng ? (
        <>
          <meta name="geo.region" content={`US-${geo.region || 'IL'}`} />
          {geo.placename && <meta name="geo.placename" content={geo.placename} />}
          <meta name="geo.position" content={`${geo.lat};${geo.lng}`} />
          <meta name="ICBM" content={`${geo.lat}, ${geo.lng}`} />
          <meta property="place:location:latitude" content={String(geo.lat)} />
          <meta property="place:location:longitude" content={String(geo.lng)} />
        </>
      ) : (
        <>
          <meta name="geo.region" content="US-IL" />
          <meta name="geo.placename" content="Chicago" />
          <meta name="geo.position" content="41.9536;-87.7484" />
        </>
      )}

      {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
      {extraSchemas.map((s, i) => (
        <script key={`extra-schema-${i}`} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
